import { esSerializablePorTipo } from "../constants/clasificacionContable";
import { client } from "./supabase";
import { exigirFechaValida } from "./validarFechaDocumento";

// =====================================================================
// Lotes de compra — bloque Stock (ver ARQUITECTURA 9.36)
//
// Un LOTE es el evento de compra (proveedor, factura, cantidad, costo unitario).
// De él cuelgan N UNIDADES en `activos` vía `activos.lote_id`.
//
// Ciclo:
//   LOTE planificado            → 1 línea en CapEx, 0 unidades existen
//   → ejecutado_sin_factura     → nacen N unidades en stock (fecha_compra null)
//   → activo                    → se propaga fecha_compra, empiezan a depreciar
//
// INVARIANTE: `estado='planificado'` vive SOLO en el lote, nunca en una unidad.
// Es lo que impide que un lote de 35 aparezca como 35 líneas en el tab CapEx
// (que consulta activos con estado='planificado').
// =====================================================================

// Fecha local YYYY-MM-DD. No usar toISOString(): en Chile/Perú (UTC-3/-5) de noche
// devuelve el día siguiente en UTC y el kardex quedaría fechado mañana.
const hoyLocal = () => {
  const d = new Date();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${mm}-${dd}`;
};

// ---------------------------------------------------------------------
// CRUD (molde de utils/activos.js)
// ---------------------------------------------------------------------

export const getLotes = async (pais, filters = {}) => {
  let query = client
    .from("lotes_compra")
    .select(
      `
      *,
      proveedor_rel:proveedores(id, nombre),
      factura_sii:facturas_sii_recibidas(id, folio, razon_social_emisor)
    `
    )
    .eq("pais", pais)
    .eq("eliminado", false)
    .order("fecha_planificada", { ascending: false });

  if (filters.estado) query = query.eq("estado", filters.estado);
  if (filters.tipo_activo) query = query.eq("tipo_activo", filters.tipo_activo);
  if (filters.proveedor_id) query = query.eq("proveedor_id", filters.proveedor_id);

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
};

export const getLoteById = async (id) => {
  const { data, error } = await client
    .from("lotes_compra")
    .select(
      `
      *,
      proveedor_rel:proveedores(id, nombre),
      factura_sii:facturas_sii_recibidas(id, folio, razon_social_emisor)
    `
    )
    .eq("id", id)
    .single();
  if (error) throw error;
  return data;
};

// F1 (B.2b, 2026-09-26): `fecha_planificada` exige `modo` explícito en `opciones` (10.190 aplicado
// al frontend). Regla 10.159: un lote SIEMPRE admite fecha pasada (sin piso) — el caller real
// (LoteCostosModal, guardarFechaLoteCapex) siempre pasa MODOS_VALIDACION.DATO_DOCUMENTO; no hay un
// modo ESTRICTO para lotes.
export const crearLote = async (lote, opciones = {}) => {
  exigirFechaValida(lote, "fecha_planificada", opciones);
  const { data, error } = await client
    .from("lotes_compra")
    .insert({ ...lote, eliminado: false })
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const actualizarLote = async (id, cambios, opciones = {}) => {
  exigirFechaValida(cambios, "fecha_planificada", opciones);
  const { data, error } = await client
    .from("lotes_compra")
    .update({ ...cambios, updated_at: new Date().toISOString() })
    .eq("id", id)
    .select()
    .single();
  if (error) throw error;
  return data;
};

export const eliminarLote = async (id) => {
  const { error } = await client
    .from("lotes_compra")
    .update({ eliminado: true, updated_at: new Date().toISOString() })
    .eq("id", id);
  if (error) throw error;
};

// Unidades de un lote (filas de `activos` con ese lote_id). Vacío mientras el lote
// es `planificado` — las unidades recién nacen al ejecutarse.
export const getUnidadesDeLote = async (loteId) => {
  const { data, error } = await client
    .from("activos")
    .select("*")
    .eq("lote_id", loteId)
    .eq("eliminado", false)
    .order("id", { ascending: true });
  if (error) throw error;
  return data || [];
};

// ---------------------------------------------------------------------
// Generación de unidades — snapshot-copy del lote
// ---------------------------------------------------------------------

// Campos que la unidad COPIA del lote. Snapshot deliberado, no join vivo: `activos`
// es lo que leen el ER y los resúmenes por edificio; si la unidad leyera el lote en
// vivo, editar un lote cambiaría números históricos ya cerrados.
const CAMPOS_HEREDADOS = [
  "tipo_activo",
  "descripcion",
  "costo_unitario",
  "moneda",
  "vida_util_meses",
  "valor_residual",
  "categoria_contable",
  "metodo_depreciacion",
  "proveedor",
  "proveedor_id",
  "numero_factura",
  "factura_sii_recibida_id",
  "pais",
];

// `activos` tiene estas columnas NOT NULL, pero en `lotes_compra` son nullable.
// Sin esta validación el insert falla con un 23502 críptico a mitad del flujo.
const REQUERIDOS_EN_ACTIVOS = ["tipo_activo", "descripcion", "costo_unitario", "moneda", "vida_util_meses", "pais"];

/**
 * Crea en `activos` las unidades de un lote ya ejecutado, más su asiento de kardex.
 *
 * Serializable (pantalla, reproductor, tótem, antena, computador, smartphone)
 *   → N filas con cantidad=1 (cada equipo tiene identidad y serie propia).
 * Resto (consumibles, partes pasivas, servicios)
 *   → 1 sola fila con cantidad=N.
 *
 * En ambos casos Σ(cantidad × costo_unitario) == cantidad_total × costo_unitario.
 *
 * Las unidades nacen en STOCK, sin serie y sin `fecha_compra` → NO deprecian todavía
 * (la depreciación corre desde `fecha_compra`, que se propaga al vincular la factura).
 *
 * NO es transaccional (PostgREST no da transacción multi-tabla): primero se insertan
 * las unidades y después el kardex. Si el kardex falla, las unidades quedan creadas
 * y se avisa — es recuperable (el asiento se puede reponer), al revés no.
 *
 * @param {object} lote fila de `lotes_compra`
 * @returns {{unidades: object[], movimientos: object[], totalUnidades: number, totalLote: number}}
 */
export const generarUnidadesDeLote = async (lote) => {
  if (!lote?.id) throw new Error("generarUnidadesDeLote: falta el lote o su id");

  const cantidadTotal = Number(lote.cantidad_total);
  if (!Number.isFinite(cantidadTotal) || cantidadTotal < 1) {
    throw new Error(`generarUnidadesDeLote: cantidad_total inválida (${lote.cantidad_total}) en el lote ${lote.id}`);
  }

  const faltantes = REQUERIDOS_EN_ACTIVOS.filter((c) => lote[c] === null || lote[c] === undefined || lote[c] === "");
  if (faltantes.length > 0) {
    throw new Error(
      `generarUnidadesDeLote: el lote ${lote.id} no tiene ${faltantes.join(", ")} (requeridos en activos)`
    );
  }

  // Idempotencia: si el lote ya generó unidades, no duplicar.
  const { data: yaExisten, error: errCheck } = await client
    .from("activos")
    .select("id")
    .eq("lote_id", lote.id)
    .eq("eliminado", false)
    .limit(1);
  if (errCheck) throw errCheck;
  if (yaExisten?.length > 0) {
    throw new Error(`generarUnidadesDeLote: el lote ${lote.id} ya tiene unidades generadas`);
  }

  const base = CAMPOS_HEREDADOS.reduce((acc, campo) => {
    acc[campo] = lote[campo];
    return acc;
  }, {});

  const unidadBase = {
    ...base,
    // valor_residual es NOT NULL con default 0 en activos; el lote puede traerlo null.
    valor_residual: lote.valor_residual ?? 0,
    lote_id: lote.id,
    estado: lote.estado,
    destino_tipo: "stock",
    destino_id: null,
    destino_empleado_id: null,
    numero_serie: null,
    fecha_compra: null,
    fecha_planificada: null,
    eliminado: false,
  };

  const serializable = esSerializablePorTipo(lote.tipo_activo);
  const filas = serializable
    ? Array.from({ length: cantidadTotal }, () => ({ ...unidadBase, cantidad: 1 }))
    : [{ ...unidadBase, cantidad: cantidadTotal }];

  // Reconciliación: lo generado tiene que valer exactamente lo que vale el lote.
  const costoUnit = Number(lote.costo_unitario);
  const totalUnidades = filas.reduce((s, f) => s + f.cantidad * costoUnit, 0);
  const totalLote = cantidadTotal * costoUnit;
  if (Math.abs(totalUnidades - totalLote) > 0.01) {
    throw new Error(
      `generarUnidadesDeLote: el total de las unidades (${totalUnidades}) no reconcilia con el lote ${lote.id} (${totalLote})`
    );
  }

  const { data: unidades, error: errUnidades } = await client.from("activos").insert(filas).select();
  if (errUnidades) throw errUnidades;

  // Kardex: un asiento de recepción por unidad creada (null → stock).
  const fecha = hoyLocal();
  const asientos = (unidades || []).map((u) => ({
    activo_id: u.id,
    fecha,
    tipo_movimiento: "recepcion",
    destino_tipo_desde: null,
    destino_id_desde: null,
    destino_tipo_hasta: "stock",
    destino_id_hasta: null,
    nota: `Recepción por ejecución del lote #${lote.id}`,
  }));

  const { data: movimientos, error: errMov } = await client.from("activos_movimientos").insert(asientos).select();
  if (errMov) {
    // Las unidades YA se crearon. No se revierten (sin transacción, un rollback manual
    // podría fallar a medias y dejar peor estado). Se avisa con el detalle para reponer.
    throw new Error(
      `Unidades del lote ${lote.id} creadas OK, pero falló el kardex: ${errMov.message}. Reponer los asientos de recepción.`
    );
  }

  return {
    unidades: unidades || [],
    movimientos: movimientos || [],
    totalUnidades,
    totalLote,
  };
};
